/**
 * CockpitPage — Marketing-Cockpit: Zusammenspiel Webseite + Google Ads + SEMrush.
 * Zeigt alle 3 Datenquellen pro Firma und generiert KI-Massnahmen-Ideen daraus.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Compass, Globe, CircleDollarSign, LineChart, Sparkles, Loader2,
  RefreshCw, Trash2, AlertTriangle, Lightbulb, Megaphone,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { CreateCampaignDialog } from "@/components/googleads/CampaignManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_BADGE = {
  ads: "bg-blue-100 text-blue-700 border-blue-200",
  seo: "bg-emerald-100 text-emerald-700 border-emerald-200",
  website: "bg-violet-100 text-violet-700 border-violet-200",
  cross: "bg-amber-100 text-amber-700 border-amber-200",
};
const CAT_LABEL = { ads: "Google Ads", seo: "SEO", website: "Website", cross: "Kanalübergreifend" };
const PRIO_BADGE = {
  hoch: "bg-red-100 text-red-700 border-red-200",
  mittel: "bg-amber-100 text-amber-700 border-amber-200",
  niedrig: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABEL = { neu: "Neu", geplant: "Geplant", erledigt: "Erledigt", verworfen: "Verworfen" };

export default function CockpitPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [draft, setDraft] = useState(null);
  const [draftIdea, setDraftIdea] = useState(null);
  const [channels, setChannels] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
  }, []);

  const load = useCallback(async (cid) => {
    if (!cid) return;
    setLoading(true);
    setData(null);
    setChannels(null);
    try {
      const [ov, id] = await Promise.all([
        axios.get(`${API}/cockpit/${cid}/overview`, { withCredentials: true }),
        axios.get(`${API}/cockpit/${cid}/ideas`, { withCredentials: true }),
      ]);
      setData(ov.data);
      setIdeas(id.data.ideas || []);
      axios.get(`${API}/cockpit/${cid}/channels`, { withCredentials: true })
        .then((r) => setChannels(r.data)).catch(() => setChannels({ channels: [] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(companyId); }, [companyId, load]);

  const generate = async () => {
    setBusy("generate");
    try {
      const r = await axios.post(`${API}/cockpit/${companyId}/ideas/generate`, {}, { withCredentials: true });
      toast.success(r.data.message + " — Admin wurde per E-Mail informiert");
      setIdeas((list) => [...(r.data.ideas || []), ...list]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Generierung fehlgeschlagen");
    } finally { setBusy(""); }
  };

  const setStatus = async (idea, status) => {
    setBusy(idea.id);
    try {
      await axios.put(`${API}/cockpit/${companyId}/ideas/${idea.id}`, { status }, { withCredentials: true });
      setIdeas((list) => list.map((x) => (x.id === idea.id ? { ...x, status } : x)));
      toast.success(status === "geplant" ? "Eingeplant — Admin wurde per E-Mail informiert" : "Status aktualisiert");
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const toCampaign = async (idea) => {
    setBusy(`draft-${idea.id}`);
    try {
      const r = await axios.post(`${API}/cockpit/${companyId}/ideas/${idea.id}/campaign-draft`, {}, { withCredentials: true });
      setDraft(r.data.draft);
      setDraftIdea(idea);
      toast.success("KI-Kampagnen-Entwurf bereit — bitte prüfen und anpassen");
    } catch (e) { toast.error(e.response?.data?.detail || "Entwurf fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const removeIdea = async (idea) => {
    if (!window.confirm(`Idee «${idea.title}» löschen?`)) return;
    setBusy(idea.id);
    try {
      await axios.delete(`${API}/cockpit/${companyId}/ideas/${idea.id}`, { withCredentials: true });
      setIdeas((list) => list.filter((x) => x.id !== idea.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(""); }
  };

  const web = data?.web, ads = data?.ads, seo = data?.seo;

  return (
    <div className="p-6 space-y-5" data-testid="cockpit-page">
      <PageHelp
        title="Marketing-Cockpit"
        text="Hier laufen Website-Analytics, Google Ads und SEMrush (SEO) pro Firma zusammen. Die KI analysiert alle drei Quellen gemeinsam und schlägt konkrete Massnahmen vor — z.B. Keywords, die organisch gut ranken aber keine Ads haben, oder Seiten mit viel Traffic ohne Kampagne. Bei jeder Ideen-Generierung und Einplanung erhält der Admin eine E-Mail."
      />
      <div className="flex flex-wrap items-center gap-3">
        <Compass className="w-6 h-6 text-[#2563EB]" />
        <h1 className="text-2xl font-bold">Marketing-Cockpit</h1>
        <span className="text-xs text-[var(--studio-text-muted)]">Webseite · Google Ads · SEMrush</span>
        <div className="flex-1" />
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => load(companyId)} disabled={loading} data-testid="cockpit-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="cockpit-company-select"><SelectValue placeholder="Firma" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading && !data && (
        <div className="py-14 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--studio-text-muted)]" /></div>
      )}

      {data && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Website */}
          <Card data-testid="cockpit-web-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4 text-violet-500" /> Website (30 Tage)
                {!web?.available && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Kein Tracking</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-lg font-bold">{web?.pageviews ?? 0}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Seitenaufrufe</p></div>
                <div><p className="text-lg font-bold">{web?.visitors ?? 0}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Besucher</p></div>
                <div><p className="text-lg font-bold">{web?.web_leads ?? 0}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Web-Leads</p></div>
              </div>
              {web?.top_pages?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-medium text-[var(--studio-text-muted)]">Top-Seiten</p>
                  {web.top_pages.slice(0, 5).map((p) => (
                    <div key={p.path} className="flex items-center justify-between text-xs">
                      <span className="truncate mr-2">{p.path}</span>
                      <span className="font-medium shrink-0">{p.views}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Google Ads */}
          <Card data-testid="cockpit-ads-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CircleDollarSign className="w-4 h-4 text-blue-500" /> Google Ads (30 Tage)
                {!ads?.connected && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Nicht verbunden</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {ads?.connected ? (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><p className="text-lg font-bold">{ads.totals.clicks}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Klicks</p></div>
                    <div><p className="text-lg font-bold">{ads.totals.cost.toFixed(0)}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Kosten</p></div>
                    <div><p className="text-lg font-bold">{ads.totals.conversions}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Conversions</p></div>
                  </div>
                  <div className="space-y-1">
                    {ads.campaigns.slice(0, 5).map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-xs gap-2">
                        <span className="truncate">{c.name}</span>
                        <span className="text-[var(--studio-text-muted)] shrink-0">{c.clicks} Kl. · {c.cost.toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-[var(--studio-text-muted)] flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  {ads?.error || "Google Ads für diese Firma noch nicht eingerichtet (Kunden-ID im Google Ads Manager hinterlegen)."}
                </p>
              )}
            </CardContent>
          </Card>

          {/* SEO */}
          <Card data-testid="cockpit-seo-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <LineChart className="w-4 h-4 text-emerald-500" /> SEO / SEMrush
                {!seo?.available && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Keine Daten</Badge>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {seo?.available ? (
                <>
                  <div className="grid grid-cols-2 gap-2 text-center">
                    <div><p className="text-lg font-bold">{seo.keyword_count}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Keywords</p></div>
                    <div><p className="text-lg font-bold">{seo.overview?.organic_traffic ?? "–"}</p><p className="text-[10px] text-[var(--studio-text-muted)]">Org. Traffic</p></div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[11px] font-medium text-[var(--studio-text-muted)]">Top-Rankings ({seo.domain})</p>
                    {seo.top_keywords.slice(0, 5).map((k) => (
                      <div key={k.keyword} className="flex items-center justify-between text-xs gap-2">
                        <span className="truncate">{k.keyword}</span>
                        <Badge variant="outline" className="text-[9px] shrink-0">Pos. {k.position}</Badge>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-[var(--studio-text-muted)] flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  Noch keine SEMrush-Daten — unter «SEO &amp; Keywords» abrufen.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Kanal-Vergleich */}
      {data && (
        <Card data-testid="cockpit-channels-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              ⚖️ Kanal-Vergleich — was bringt ein Franken Werbebudget? <span className="text-xs font-normal text-[var(--studio-text-muted)]">(letzte 30 Tage)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!channels && <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
            {channels && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs text-[var(--studio-text-muted)] border-b border-[var(--studio-border)]">
                      <th className="text-left py-2 pr-3 font-medium">Kanal</th>
                      <th className="text-right py-2 px-3 font-medium">Kosten</th>
                      <th className="text-right py-2 px-3 font-medium">Klicks</th>
                      <th className="text-right py-2 px-3 font-medium">Leads</th>
                      <th className="text-right py-2 px-3 font-medium">CHF/Klick</th>
                      <th className="text-right py-2 px-3 font-medium">CHF/Lead</th>
                      <th className="text-right py-2 pl-3 font-medium">Leads pro 100 CHF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.channels.map((c) => (
                      <tr key={c.key} className={`border-b border-[var(--studio-border)] last:border-0 ${channels.best === c.key ? "bg-emerald-50/60" : ""}`} data-testid={`cockpit-channel-row-${c.key}`}>
                        <td className="py-2 pr-3">
                          <span className="font-medium">{c.label}</span>
                          {channels.best === c.key && <Badge className="ml-2 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">🏆 Bester Kanal</Badge>}
                          {!c.paid && <Badge variant="outline" className="ml-2 text-[9px]">gratis{c.links ? ` · ${c.links} Link${c.links > 1 ? "s" : ""}` : ""}</Badge>}
                          {c.paid && !c.connected && <Badge variant="outline" className="ml-2 text-[9px] text-slate-400">nicht verbunden</Badge>}
                        </td>
                        <td className="text-right py-2 px-3">{c.paid ? `CHF ${c.cost.toFixed(2)}` : "—"}</td>
                        <td className="text-right py-2 px-3 font-medium">{c.clicks}</td>
                        <td className="text-right py-2 px-3 font-medium">{c.leads}</td>
                        <td className="text-right py-2 px-3">{c.cpc != null ? c.cpc.toFixed(2) : "—"}</td>
                        <td className="text-right py-2 px-3">{c.cost_per_lead != null ? c.cost_per_lead.toFixed(2) : "—"}</td>
                        <td className="text-right py-2 pl-3">{c.leads_per_100 != null ? c.leads_per_100.toFixed(1) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-[var(--studio-text-muted)] mt-2">
                  Leads werden über Klick-IDs (gclid/fbclid) und Tracking-Link-Codes zugeordnet — Voraussetzung: Tracking-Snippet auf der Website. Gratis-Kanäle stammen aus deinen individuellen Tracking-Links (Flyer, QR-Codes …).
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* KI-Ideen */}
      <Card data-testid="cockpit-ideas-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex flex-wrap items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" /> KI-Massnahmen aus dem Zusammenspiel
            <div className="flex-1" />
            <Button size="sm" onClick={generate} disabled={busy === "generate" || !data} className="gap-1" data-testid="cockpit-generate-btn">
              {busy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              KI-Ideen generieren
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {ideas.length === 0 && (
            <p className="text-sm text-[var(--studio-text-muted)]" data-testid="cockpit-ideas-empty">
              Noch keine Ideen. Die KI vergleicht Website-Traffic, Ads-Performance und SEMrush-Rankings und schlägt
              konkrete Massnahmen vor — z.B. «Keyword X rankt organisch auf Platz 12 → mit Ads pushen».
            </p>
          )}
          {ideas.map((idea) => (
            <div key={idea.id} className="border border-[var(--studio-border)] rounded-lg p-3 space-y-1.5" data-testid={`cockpit-idea-${idea.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{idea.title}</span>
                <Badge variant="outline" className={`text-[9px] ${CAT_BADGE[idea.category] || ""}`}>{CAT_LABEL[idea.category] || idea.category}</Badge>
                <Badge variant="outline" className={`text-[9px] ${PRIO_BADGE[idea.priority] || ""}`}>Prio: {idea.priority}</Badge>
                <div className="flex-1" />
                {(idea.category === "ads" || idea.category === "cross") && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                    disabled={busy === `draft-${idea.id}` || !data?.ads?.connected}
                    title={!data?.ads?.connected ? "Google Ads für diese Firma noch nicht verbunden" : "KI erstellt einen fertigen Kampagnen-Entwurf aus dieser Idee"}
                    onClick={() => toCampaign(idea)} data-testid={`cockpit-idea-campaign-${idea.id}`}>
                    {busy === `draft-${idea.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
                    Zu Kampagne
                  </Button>
                )}
                <Select value={idea.status} onValueChange={(v) => setStatus(idea, v)}>
                  <SelectTrigger className="h-7 w-32 text-xs" data-testid={`cockpit-idea-status-${idea.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-7 px-1.5 text-red-500" disabled={busy === idea.id}
                  onClick={() => removeIdea(idea)} data-testid={`cockpit-idea-delete-${idea.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-xs text-[var(--studio-text-muted)]">{idea.description}</p>
              {idea.action && <p className="text-xs"><b>Nächster Schritt:</b> {idea.action}</p>}
            </div>
          ))}
        </CardContent>
      </Card>

      <CreateCampaignDialog
        open={!!draft}
        onClose={() => { setDraft(null); setDraftIdea(null); }}
        companyId={companyId}
        initial={draft}
        onCreated={() => {
          if (draftIdea) setStatus(draftIdea, "geplant");
          setDraft(null); setDraftIdea(null);
          toast.success("Kampagne pausiert erstellt — im Google Ads Manager prüfen und aktivieren");
        }}
      />
    </div>
  );
}
